import React, { useState, useEffect } from 'react';
import { X, UploadCloud, CheckCircle2, AlertCircle } from 'lucide-react';
import { SubmissionFolder, User } from '../types';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from '../apiEnv';

interface SubmitDeckModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  deck: {
    id: string;
    name: string;
    tricks: string[];
  } | null;
}

const SubmitDeckModal: React.FC<SubmitDeckModalProps> = ({ isOpen, onClose, user, deck }) => {
  const [folders, setFolders] = useState<SubmissionFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [folderPassword, setFolderPassword] = useState('');
  const [competitionNumber, setCompetitionNumber] = useState('');
  const [initialScore, setInitialScore] = useState('0');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchFolders();
      setMessage(null);
      setSelectedFolder('');
      setFolderPassword('');
      setCompetitionNumber('');
      setInitialScore('0');
    }
  }, [isOpen]);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        isLocalBackend() ? '/api/admin/submission-folders' : phpApiUrl('admin_get_submission_folders'),
        {
          credentials: 'include',
          headers: phpAuthHeaders()
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setFolders(data.folders || []);
      }
    } catch (err) {
      console.error('Failed to fetch folders', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const normalizedCompetitionNumber = competitionNumber.trim();
    const normalizedFolderPassword = folderPassword.trim();
    const normalizedInitialScore = initialScore.trim() === '' ? 0 : Number(initialScore);
    if (!deck || !selectedFolder) return;
    if (!normalizedCompetitionNumber) {
      setMessage({ type: 'error', text: 'Competition number is required. / 请填写比赛编号。' });
      return;
    }
    if (!normalizedFolderPassword) {
      setMessage({ type: 'error', text: 'Folder password is required. / 请填写赛事文件夹密码。' });
      return;
    }
    if (!Number.isFinite(normalizedInitialScore)) {
      setMessage({ type: 'error', text: 'Initial score must be a valid number. / 初始分必须是有效数字。' });
      return;
    }
    
    // Ensure the deck has exactly 30 tricks if that's a requirement.
    // The user's prompt mentions "如图所示数字为招式顺序... 30", so it's a 30-trick deck.
    // Let's just submit the first 30 tricks, or if it has fewer, submit what it has.
    
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(
        isLocalBackend() ? '/api/user/submissions' : phpApiUrl('user_create_submission'),
        {
          method: 'POST',
          headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({
            folderId: selectedFolder,
            folderPassword: normalizedFolderPassword,
            competitionNumber: normalizedCompetitionNumber,
            initialScore: normalizedInitialScore,
            deckName: deck.name,
            tricks: deck.tricks.slice(0, 30) // Take up to 30 tricks
          })
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage({ type: 'success', text: 'Submitted successfully! / 提交成功！' });
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setMessage({ type: 'error', text: data.error || 'Submission failed' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !deck) return null;

  const openFolders = folders.filter(folder => folder.isSubmissionOpen !== false);
  const selectedFolderConfig = folders.find(folder => folder.id === selectedFolder) || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in duration-300">
      <div className="relative w-full max-w-md bg-zinc-900 border border-blue-500/30 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(59,130,246,0.15)] font-rajdhani text-white">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
          <X size={24} />
        </button>

        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 bg-blue-600/20 border border-blue-500/50 rounded-full flex items-center justify-center mb-4 text-blue-400">
            <UploadCloud size={24} />
          </div>
          <h2 className="text-2xl font-orbitron font-bold text-center uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
            Submit Deck / 提交招式
          </h2>
          <p className="text-zinc-400 mt-2 text-sm text-center">
            Submit <span className="text-white font-bold">{deck.name}</span> ({deck.tricks.length} tricks) to an event.
          </p>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 font-orbitron text-sm tracking-widest ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/50 text-green-400' : 'bg-red-500/10 border border-red-500/50 text-red-400'}`}>
            {message.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            {message.text}
          </div>
        )}

        <div className="space-y-4">
          <label className="block text-zinc-400 font-orbitron text-sm tracking-widest">Select Event Folder / 选择提交赛事</label>
          {loading && folders.length === 0 ? (
            <div className="text-center text-zinc-500 py-4 font-orbitron text-sm animate-pulse">LOADING FOLDERS...</div>
          ) : folders.length === 0 ? (
            <div className="text-center text-zinc-500 py-4 font-orbitron text-sm">No active events found. / 暂无可提交的赛事。</div>
          ) : (
            <>
              <select
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
                className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:border-blue-500 focus:outline-none transition-colors"
              >
                <option value="" disabled>-- Choose an event --</option>
                {folders.map(f => (
                  <option key={f.id} value={f.id} disabled={f.isSubmissionOpen === false}>
                    {f.name}
                    {f.isSubmissionOpen === false ? ' (Closed / 已关闭)' : ''}
                    {f.hasPassword === false ? ' (No Password / 未设密码)' : ''}
                  </option>
                ))}
              </select>
              {openFolders.length === 0 && (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-red-300 text-sm">
                  All folders are currently closed for submissions. / 当前所有文件夹都已关闭提交。
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-zinc-400 font-orbitron text-sm tracking-widest mb-2">
              Event Folder Password / 赛事文件夹密码
            </label>
            <input
              type="password"
              value={folderPassword}
              onChange={(e) => setFolderPassword(e.target.value)}
              placeholder="Enter event folder password / 填写赛事文件夹密码"
              className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
            {selectedFolderConfig?.hasPassword === false && (
              <div className="mt-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-200 text-sm">
                This event has no upload password configured yet. / 当前赛事还没有配置上传密码，请联系管理员。
              </div>
            )}
          </div>

          <div>
            <label className="block text-zinc-400 font-orbitron text-sm tracking-widest mb-2">
              Competition Number / 比赛编号
            </label>
            <input
              type="text"
              value={competitionNumber}
              onChange={(e) => setCompetitionNumber(e.target.value)}
              placeholder="Enter your competition number / 填写你的比赛编号"
              className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:border-blue-500 focus:outline-none transition-colors"
              maxLength={32}
            />
          </div>

          <div>
            <label className="block text-zinc-400 font-orbitron text-sm tracking-widest mb-2">
              Initial Score / 初始分
            </label>
            <input
              type="number"
              value={initialScore}
              onChange={(e) => setInitialScore(e.target.value)}
              placeholder="Enter initial score / 填写初始分"
              className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading || !selectedFolder || !folderPassword.trim() || !competitionNumber.trim() || openFolders.length === 0}
            className="w-full mt-6 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-orbitron font-bold tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(37,99,235,0.3)] hover:shadow-[0_0_25px_rgba(37,99,235,0.5)]"
          >
            {loading ? 'PROCESSING...' : <><UploadCloud size={18} /> SUBMIT NOW</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmitDeckModal;
